import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Grid, Input, MenuItem, Select, SelectProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { Filter } from '../../../../components/filter/filter';
import { FilterButton, FilterField } from '../../../../components/filter/types';
import { InfoFieldChangeHandler } from '../../../../components';

import { localToUTC } from '../../../../utils/dates';
import { TransactionDirection, TransactionStatus } from '../../../../enums';
import {
  type GetCoinsResult,
  type GetProfileTransactionsDTO,
  type GetProfileWalletsResult,
  type GetTransactionsDTO,
} from '../../../../api';
import type { TransactionsComponentType } from '../../types';
import type { TODO_ANY } from '../../../../types';

type TransactionsFilterProps = {
  componentType: TransactionsComponentType;
  defaultFilterValues: Partial<GetTransactionsDTO['params']> | null;
  setDefaultFilterValues: Dispatch<SetStateAction<Partial<GetTransactionsDTO['params']> | null>>;
  profileWalletUid: string;
  setProfileWalletUid: Dispatch<SetStateAction<string>>;
  withdrawalsFilterValues: Partial<GetTransactionsDTO['params']> | null;
  setWithdrawalsFilterValues: Dispatch<
    SetStateAction<Partial<GetTransactionsDTO['params']> | null>
  >;
  setSearchingStatus: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  originalTransactionsArgs: GetTransactionsDTO | undefined;
  originalProfileTransactionsArgs: GetProfileTransactionsDTO | undefined;
  isProfileTransactionsUninitialized: boolean;
  profileWallets: GetProfileWalletsResult | undefined;
  coins: GetCoinsResult | undefined;
  getTransactions: TODO_ANY;
  getProfileTransactions: TODO_ANY;
};

export const TransactionsFilter: FC<TransactionsFilterProps> = (props) => {
  const {
    componentType,
    defaultFilterValues,
    setDefaultFilterValues,
    profileWalletUid,
    setProfileWalletUid,
    withdrawalsFilterValues,
    setWithdrawalsFilterValues,
    setSearchingStatus,
    setPage,
    originalTransactionsArgs,
    originalProfileTransactionsArgs,
    isProfileTransactionsUninitialized,
    profileWallets,
    coins,
    getTransactions,
    getProfileTransactions,
  } = props;
  const { t } = useTranslation();
  const { state } = useLocation();

  // for 'default' filter logic
  const handleDefaultFilterValues: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;

    setDefaultFilterValues((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  const searchByDefaultFilter = async () => {
    if (!originalTransactionsArgs || !defaultFilterValues) return;

    const args = {
      ...originalTransactionsArgs,
      params: {
        ...originalTransactionsArgs.params,
        page: 1,

        ...defaultFilterValues,
        // @ts-ignore
        group: defaultFilterValues.group === 'all' ? undefined : defaultFilterValues.group,
        date_from: defaultFilterValues?.date_from
          ? localToUTC(defaultFilterValues.date_from, 'DD.MM.YYYY')
          : undefined,
        date_to: defaultFilterValues?.date_to
          ? localToUTC(defaultFilterValues.date_to, 'DD.MM.YYYY')
          : undefined,
      },
    };

    try {
      await getTransactions(args).unwrap();
      setSearchingStatus(true);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const resetDefaultFilter = async () => {
    if (!originalTransactionsArgs || !defaultFilterValues) return;

    try {
      defaultFilterValues &&
        (await getTransactions({
          ...originalTransactionsArgs,
          params: {
            ...originalTransactionsArgs.params,
            page: 1,
            group: undefined,
            application_uid: undefined,
            direction: undefined,
            transaction_id: undefined,
            status: undefined,
            coin_id: undefined,
            date_from: undefined,
            date_to: undefined,
          },
        }).unwrap());
      setDefaultFilterValues(null);
      setSearchingStatus(false);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const defaultFilterFields: FilterField[] = [
    {
      name: 'transaction_id',
      title: t('transactions.filter.default.id'),
      component: (
        <Input
          name="transaction_id"
          value={defaultFilterValues?.transaction_id ?? ''}
          onChange={(e) => handleDefaultFilterValues(e)}
          sx={{ width: '80px' }}
        />
      ),
    },
    {
      name: 'status',
      title: t('transactions.filter.default.status'),
      component: (
        <Select
          name="status"
          value={defaultFilterValues?.status ?? ''}
          onChange={(e) => handleDefaultFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          {Object.values(TransactionStatus).map((it) => {
            return (
              <MenuItem key={it} value={it}>
                {t(`common.transaction-status.${it}`)}
              </MenuItem>
            );
          })}
        </Select>
      ),
    },
    {
      name: 'coin_id',
      title: t('transactions.filter.default.coin-id'),
      component: (
        <Select
          name="coin_id"
          value={defaultFilterValues?.coin_id ?? ''}
          onChange={(e) => handleDefaultFilterValues(e)}
          variant="standard"
          sx={{ width: '180px' }}
        >
          {coins?.coins.map((coin) => {
            return (
              <MenuItem key={coin.id} value={coin.id}>
                {coin.name}
              </MenuItem>
            );
          })}
        </Select>
      ),
    },
    {
      name: 'direction',
      title: t('transactions.filter.default.direction'),
      component: (
        <Select
          name="direction"
          value={defaultFilterValues?.direction ?? ''}
          onChange={(e) => handleDefaultFilterValues(e)}
          variant="standard"
          sx={{ width: '180px' }}
        >
          {Object.values(TransactionDirection).map((it) => {
            return (
              <MenuItem key={it} value={it}>
                {t(`common.transaction-direction.${it}`)}
              </MenuItem>
            );
          })}
        </Select>
      ),
    },
    {
      name: 'group',
      title: t('transactions.filter.default.group'),
      component: (
        <Select
          name="group"
          value={defaultFilterValues?.group ?? ''}
          onChange={(e) => handleDefaultFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          <MenuItem value="all">{t('common.group-filter.all')}</MenuItem>
          <MenuItem value="own">{t('common.group-filter.own')}</MenuItem>
          <MenuItem value="app">{t('common.group-filter.ext')}</MenuItem>
        </Select>
      ),
    },
    {
      name: 'date_from',
      title: t('transactions.filter.default.date-from'),
      component: (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            name="date_from"
            value={
              defaultFilterValues?.date_from ? dayjs(defaultFilterValues.date_from as string) : null
            }
            onChange={(newValue) => handleDefaultFilterValues(newValue, true, 'date_from')}
            format="DD.MM.YYYY"
            disableFuture
            label={t('transactions.filter.default.date-from')}
            slotProps={{
              textField: { sx: { width: '180px' }, variant: 'standard' },
            }}
          />
        </LocalizationProvider>
      ),
      withoutLabel: true,
    },
    {
      name: 'date_to',
      title: t('transactions.filter.default.date-to'),
      component: (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            name="date_to"
            value={
              defaultFilterValues?.date_to ? dayjs(defaultFilterValues.date_to as string) : null
            }
            onChange={(newValue) => handleDefaultFilterValues(newValue, true, 'date_to')}
            format="DD.MM.YYYY"
            disableFuture
            label={t('transactions.filter.default.date-to')}
            slotProps={{
              textField: { sx: { width: '180px' }, variant: 'standard' },
            }}
          />
        </LocalizationProvider>
      ),
      withoutLabel: true,
    },
  ];
  const defaultFilterButtons: FilterButton[] = [
    {
      name: 'search',
      text: t('ui.filter.search'),
      disabled: defaultFilterValues === null,
      onClick: () => searchByDefaultFilter(),
    },
    {
      name: 'reset-filter',
      text: t('ui.filter.reset'),
      onClick: () => resetDefaultFilter(),
    },
  ];

  // get wallet uid from location state for profile filter
  useEffect(() => {
    if (!state?.filterWalletUid || isProfileTransactionsUninitialized) return;

    void searchByProfileFilter();
  }, [isProfileTransactionsUninitialized]);

  // for 'profile' filter logic
  const handleProfileFilterFields: SelectProps['onChange'] = (e) => {
    setProfileWalletUid(e.target.value as string);
  };
  const searchByProfileFilter = async () => {
    if (!profileWalletUid || !originalProfileTransactionsArgs) return;

    try {
      await getProfileTransactions({
        ...originalProfileTransactionsArgs,
        params: {
          ...originalProfileTransactionsArgs.params,
          page: 1,
          wallet_uid: profileWalletUid,
        },
      }).unwrap();
      setSearchingStatus(true);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const resetProfileFilter = async () => {
    if (!originalProfileTransactionsArgs || !profileWalletUid) return;

    try {
      profileWalletUid &&
        (await getProfileTransactions({
          ...originalProfileTransactionsArgs,
          params: {
            ...originalProfileTransactionsArgs.params,
            page: 1,
            wallet_uid: undefined,
          },
        }).unwrap());
      setProfileWalletUid('');
      setSearchingStatus(false);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const profileFilterFields: FilterField[] = [
    {
      name: 'wallet',
      title: t('transactions.filter.for-profile.wallet'),
      component: (
        <Select
          name="wallet"
          value={profileWalletUid}
          onChange={handleProfileFilterFields}
          variant="standard"
          sx={{ minWidth: '280px', maxWidth: '360px' }}
          fullWidth
        >
          {profileWallets?.wallets
            ?.slice()
            .sort((walletA, walletB) => walletA?.type.localeCompare(walletB.type))
            .map((wallet) => {
              return (
                <MenuItem key={wallet.uid} value={wallet.uid}>
                  {`${t(`transactions.filter.for-profile.${wallet.type}`)} / ${wallet.coin.name}`}
                </MenuItem>
              );
            })}
        </Select>
      ),
    },
  ];
  const profileFilterButtons: FilterButton[] = [
    {
      name: 'search',
      text: t('ui.filter.search'),
      disabled: profileWalletUid === '',
      onClick: () => searchByProfileFilter(),
    },
    {
      name: 'reset-filter',
      text: t('ui.filter.reset'),
      onClick: () => resetProfileFilter(),
    },
  ];

  // for 'withdrawals' filter logic
  const handleWithdrawalsFilterValues: InfoFieldChangeHandler = (e) => {
    // @ts-ignore
    const name = e.target.name;
    // @ts-ignore
    const value = e.target.value;

    setWithdrawalsFilterValues((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  const searchByWithdrawalsFilter = async () => {
    if (!originalTransactionsArgs || !withdrawalsFilterValues) return;

    const args = {
      ...originalTransactionsArgs,
      params: {
        ...originalTransactionsArgs.params,
        page: 1,
        direction: TransactionDirection.OUT,
        status: TransactionStatus.PENDING,

        ...withdrawalsFilterValues,
        // @ts-ignore
        group: withdrawalsFilterValues.group === 'all' ? undefined : withdrawalsFilterValues.group,
      },
    };

    try {
      await getTransactions(args).unwrap();
      setSearchingStatus(true);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const resetWithdrawalsFilter = async () => {
    if (!originalTransactionsArgs || !withdrawalsFilterValues) return;

    try {
      withdrawalsFilterValues &&
        (await getTransactions({
          ...originalTransactionsArgs,
          params: {
            ...originalTransactionsArgs.params,
            page: 1,
            direction: TransactionDirection.OUT,
            status: TransactionStatus.PENDING,

            group: undefined,
            application_uid: undefined,
            transaction_id: undefined,
            coin_id: undefined,
            date_from: undefined,
            date_to: undefined,
          },
        }).unwrap());
      setWithdrawalsFilterValues(null);
      setSearchingStatus(false);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const withdrawalsFilterFields: FilterField[] = [
    {
      name: 'group',
      title: t('transactions.filter.for-withdrawals.group'),
      component: (
        <Select
          name="group"
          value={withdrawalsFilterValues?.group ?? ''}
          onChange={(e) => handleWithdrawalsFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          <MenuItem value="all">{t('common.group-filter.all')}</MenuItem>
          <MenuItem value="own">{t('common.group-filter.own')}</MenuItem>
          <MenuItem value="app">{t('common.group-filter.ext')}</MenuItem>
        </Select>
      ),
    },
  ];
  const withdrawalsFilterButtons: FilterButton[] = [
    {
      name: 'search',
      text: t('ui.filter.search'),
      disabled: profileWalletUid === null,
      onClick: () => searchByWithdrawalsFilter(),
    },
    {
      name: 'reset-filter',
      text: t('ui.filter.reset'),
      onClick: () => resetWithdrawalsFilter(),
    },
  ];

  // elements
  const filterFields = {
    default: defaultFilterFields,
    forProfile: profileFilterFields,
    forWithdrawals: withdrawalsFilterFields,
  }[componentType];
  const filterButtons = {
    default: defaultFilterButtons,
    forProfile: profileFilterButtons,
    forWithdrawals: withdrawalsFilterButtons,
  }[componentType];

  return (
    <Grid size={{ xs: 12 }}>
      <Filter filterFields={filterFields} filterButtons={filterButtons} />
    </Grid>
  );
};
